import React, { type FunctionComponent } from 'react'
import { type ApolloError } from 'apollo-client'
import { Tooltip, type GridColumns, Button } from '@retailer-platform/shared-components'
import { SendIcon } from '@instacart/ids-core'
import { useDomainMessages } from 'domains/account-management/utils/domain/intl'
import { type EnterpriseAccountDatum } from 'domains/account-management/components/accounts-list/utils/AccountManagementListAccounts.types'
import {
  DefaultCell,
  DefaultHeader,
} from 'domains/account-management/components/accounts-list/components/cells/ListAccountsDefaultCells'
import { DisableAccountButton } from 'domains/account-management/components/accounts-list/components/tools/DisableAccountButton'
import { EnableAccountButton } from 'domains/account-management/components/accounts-list/components/tools/EnableAccountButton'
import { EditAccountButton } from 'domains/account-management/components/accounts-list/components/tools/EditAccountButton'
import { passwordResetLink } from 'domains/account-management/utils/accountManagementUtils'
import { ListAccountsTruncatableCell } from 'domains/account-management/components/accounts-list/components/cells/ListAccountsTruncatableCell'
import { ListRolesCell } from 'domains/account-management/components/accounts-list/components/cells/ListRolesCell'
import { PaginatedGrid, defaultItemsPerPageValues } from '@retailer-platform/shared-components'
import { usePaginationQueryParams } from 'utils/query-params/usePaginationQueryParams.hooks'

interface Props {
  accounts: EnterpriseAccountDatum[]
  totalPages: number
  loading: boolean
  error?: ApolloError
  retailerScoped?: boolean
  refetchAccounts?: () => void
}

export const accountButtonStyle = {
  minWidth: '28px',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const listAccountsGridListTestIds = {
  editButton: 'list-account-grid-list-edit-button',
  disableButton: 'list-account-grid-list-disable-button',
}

export const ListAccountsGridList: FunctionComponent<Props> = ({
  loading,
  error,
  accounts,
  totalPages,
  refetchAccounts,
}) => {
  const [pageQueryParams, setPage, setItemsPerPage] = usePaginationQueryParams()
  const { page, itemsPerPage } = pageQueryParams

  const {
    accountStatusPendingSetup,
    accountStatusPendingSetupDetails,
    accountStatusDeactivated,
    accountStatusActivated,
    passwordResetTooltip,
  } = useDomainMessages({
    accountStatusPendingSetup: 'accountManagementDomain.list.statuses.pendingSetup',
    accountStatusPendingSetupDetails: 'accountManagementDomain.list.statuses.pendingSetup.details',
    accountStatusDeactivated: 'accountManagementDomain.list.statuses.deactivated',
    accountStatusActivated: 'accountManagementDomain.list.statuses.activated',
    passwordResetTooltip: 'accountManagementDomain.list.passwordResetTooltip',
  })

  if (error) return null

  const columns: GridColumns<EnterpriseAccountDatum> = [
    {
      id: 'name',
      width: 120,
      Cell: ({ row }) => (
        <ListAccountsTruncatableCell>
          {`${row.original.givenName} ${row.original.surname}`}
        </ListAccountsTruncatableCell>
      ),
    },
    {
      id: 'email',
      width: 160,
    },
    {
      id: 'title',
      width: 70,
    },
    {
      id: 'roles',
      width: 160,
      Cell: ({ row }) => <ListRolesCell roles={row.original.roles} />,
    },
    {
      id: 'status',
      width: 80,
      Cell: ({ row }) => (
        <React.Fragment>
          {row.original.accountStateValid && row.original.disabled && (
            <span>{accountStatusDeactivated}</span>
          )}
          {!row.original.accountStateValid && (
            <span title={accountStatusPendingSetupDetails}>{accountStatusPendingSetup}</span>
          )}
          {!row.original.disabled && row.original.accountStateValid && (
            <span>{accountStatusActivated}</span>
          )}
        </React.Fragment>
      ),
      Header: DefaultHeader,
    },
    {
      id: 'tools',
      width: 90,
      Header: '',
      Cell: ({ row }) => (
        <React.Fragment>
          {!row.original.disabled && (
            <DisableAccountButton
              refetchAccounts={refetchAccounts}
              accountCanonicalId={row.original.canonicalId}
              target={`${row.original.givenName} ${row.original.surname} (${row.original.email})`}
              data-testid={listAccountsGridListTestIds.disableButton}
              key={row.original.canonicalId}
            />
          )}
          {row.original.disabled && (
            <EnableAccountButton
              refetchAccounts={refetchAccounts}
              accountCanonicalId={row.original.canonicalId}
              target={`${row.original.givenName} ${row.original.surname} (${row.original.email})`}
              key={row.original.canonicalId}
            />
          )}
          {!row.original.disabled && (
            <EditAccountButton
              accountCanonicalId={row.original.canonicalId}
              data-testid={listAccountsGridListTestIds.editButton}
            />
          )}
          {!row.original.accountStateValid && (
            <Tooltip
              placement="bottom"
              target={
                <a
                  href={passwordResetLink({
                    authService: row.original.authServiceRequired,
                    email: row.original.email,
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit' }}
                >
                  <Button variant={'tertiary'} compact={true} css={accountButtonStyle}>
                    <SendIcon size={16} color="systemGrayscale70" />
                  </Button>
                </a>
              }
            >
              {passwordResetTooltip}
            </Tooltip>
          )}
        </React.Fragment>
      ),
    },
  ].map(column => ({
    ...column,
    Cell: column.Cell ?? DefaultCell,
    Header: column.Header ?? DefaultHeader,
  }))

  return (
    <PaginatedGrid
      data={accounts || []} // PaginatedGrid doesn't like null or undefined data
      columns={columns}
      page={page}
      isLoading={loading}
      onPageChange={setPage}
      totalPages={totalPages || Number.POSITIVE_INFINITY}
      emptyStateShowHeader={false}
      itemsPerPage={itemsPerPage}
      itemsPerPageValues={defaultItemsPerPageValues}
      loadingRowsCount={itemsPerPage}
      onItemsPerPageChange={setItemsPerPage}
      showItemsPerPageSelector={true}
    />
  )
}
